// src/pages/Projects/Projects.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePublishedProjects } from '../../hooks/useProjects';
import CachedImage from '../../components/CachedImage/CachedImage';
import './Projects.css';

// Project data

function Projects() {
  // Get location to check for filter parameter
  const location = useLocation();
  const navigate = useNavigate();
  
  // Fetch projects from Firebase
  const { projects, loading, error } = usePublishedProjects();

  // State for filter
  const [activeFilter, setActiveFilter] = useState('All');

  // Filtered projects based on active filter
  const [filteredProjects, setFilteredProjects] = useState([]);
  
  // Dynamically generate categories from all tags in projects
  const [categories, setCategories] = useState(['All']);

  // Check for filter parameter on component mount and location change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const filterParam = searchParams.get('filter');
    
    if (filterParam && categories.includes(filterParam)) {
      setActiveFilter(filterParam);
    } else {
      setActiveFilter('All');
    }
  }, [location.search, categories]);

  // Update categories when projects change
  useEffect(() => {
    if (projects.length > 0) {
      const allTags = projects.flatMap(project => project.tags || []);
      const uniqueTags = [...new Set(allTags)];
      setCategories(['All', ...uniqueTags]);
    }
  }, [projects]);

  // Update filtered projects when the active filter changes
  useEffect(() => {
    if (activeFilter === 'All') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.tags && project.tags.includes(activeFilter));
      setFilteredProjects(filtered);
    }
  }, [activeFilter, projects]);

  // Handle filter change
  const handleFilterChange = (category) => {
    setActiveFilter(category);
    
    // Update URL
    if (category === 'All') {
      navigate('/projects', { replace: true });
    } else {
      navigate(`/projects?filter=${category}`, { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="projects-page">
        <div className="projects-header">
          <h1>My Projects</h1>
        </div>
        <div className="projects-container">
          <div className="loading">Loading projects...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="projects-page">
        <div className="projects-header">
          <h1>My Projects</h1>
        </div>
        <div className="projects-container">
          <div className="error">
            <h3>Error loading projects</h3>
            <p>{error}</p>
            <p>Please check your Firebase configuration.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-page">
      <div className="projects-header">
        <h1>My Projects</h1>
      </div>

      <div className="projects-container">
        <div className="projects-content">
          <p>
            Here are some of the projects I’ve worked on during my academic, personal, and professional journey.
            From developing robotic applications to building custom hardware, each project has helped me deepen my skills in robotics and autonomous systems.
          </p>
          <div className="projects-filter">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-button btn-secondary ${activeFilter === category ? 'active' : ''}`}
                onClick={() => handleFilterChange(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="projects-grid-wrapper">
          <div className="projects-grid">
          {filteredProjects.length > 0 ? (
            filteredProjects.map(project => (
              <Link
                to={`/projects/${project.id}`}
                key={project.id}
                className="project-card-link"
              >
                <div className="project-card">
                  <div className="project-image">
                    {project.featured && (
                      <div className="featured-badge">★ Featured Project</div>
                    )}
                    {project.imageUrl ? (
                      <CachedImage
                        src={project.imageUrl.startsWith('http') ? project.imageUrl : (project.imageUrl.startsWith('/') ? project.imageUrl : `/${project.imageUrl}`)}
                        thumbnailSrc={project.thumbnailUrl}
                        alt={project.title}
                      />
                    ) : (
                      <div className="no-image">Project Image</div>
                    )}
                  </div>
                  <div className="project-content">
                    <h4 className="subtitle-line">{project.title}</h4>
                    <div className="project-footer">
                      <div className="project-tags">
                        {project.tags && project.tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                      <div className="view-details">View Details →</div>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="no-projects">
              <h3>No projects found</h3>
              <p>There are no projects matching the selected filter.</p>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Projects;